import { useState, useEffect, useMemo } from "react";
import { Alert, CircularProgress, Chip } from "@mui/material";
import { Button } from "../../../Components/ui/buttons/button";
import Typography from "../../../Components/Typography";
import { Field, FieldGroup } from "../../../Components/ui/shadcn/forms/field";
import StyledCheckbox from "../../../Components/ui/styled/styled-checkbox";

// Feature labels in Portuguese
const FEATURE_LABELS: Record<string, string> = {
  view_stations: "Visualizar Estações",
  view_statistics: "Visualizar Estatísticas",
  download_public_maps: "Download de Mapas Públicos",
  view_layers: "Visualizar Camadas",
  upload_shapefiles: "Upload de Shapefiles",
  crop_layers: "Recortar Camadas",
  reproject_layers: "Reprojetar Camadas",
  export_data: "Exportar Dados",
  draw_features: "Desenhar Feições",
  manage_layer_catalog: "Gerenciar Catálogo de Camadas",
  clear_cache: "Limpar Cache",
  manage_users: "Gerenciar Usuários",
  manage_permissions: "Gerenciar Permissões",
};

// Group features by category
const FEATURE_CATEGORIES: Record<string, string[]> = {
  Visualização: [
    "view_stations",
    "view_statistics",
    "view_layers",
    "download_public_maps",
  ],
  Processamento: [
    "upload_shapefiles",
    "crop_layers",
    "reproject_layers",
    "export_data",
    "draw_features",
  ],
  Administração: [
    "manage_layer_catalog",
    "clear_cache",
    "manage_users",
    "manage_permissions",
  ],
};

interface FeatureChecklistProps {
  selectedFeatures: string[];
  availableFeatures: string[];
  onSave: (features: string[]) => Promise<void>;
  loading?: boolean;
  disabled?: boolean;
  disabledMessage?: string;
}

export default function FeatureChecklist({
  selectedFeatures: initialSelected,
  availableFeatures,
  onSave,
  loading = false,
  disabled = false,
  disabledMessage,
}: FeatureChecklistProps) {
  const [selectedFeatures, setSelectedFeatures] =
    useState<string[]>(initialSelected);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Update when initial selection changes
  useEffect(() => {
    setSelectedFeatures(initialSelected);
  }, [initialSelected]);

  // Group available features by category
  const featuresByCategory = useMemo(() => {
    const grouped: Record<string, string[]> = {};

    Object.entries(FEATURE_CATEGORIES).forEach(([category, features]) => {
      const available = features.filter((f) => availableFeatures.includes(f));
      if (available.length > 0) {
        grouped[category] = available;
      }
    });

    // Add any uncategorized features
    const categorizedFeatures = Object.values(FEATURE_CATEGORIES).flat();
    const uncategorized = availableFeatures.filter(
      (f) => !categorizedFeatures.includes(f)
    );
    if (uncategorized.length > 0) {
      grouped["Outros"] = uncategorized;
    }

    return grouped;
  }, [availableFeatures]);

  const handleToggleFeature = (feature: string) => {
    if (disabled) return;

    setSelectedFeatures((prev) => {
      if (prev.includes(feature)) {
        return prev.filter((f) => f !== feature);
      } else {
        return [...prev, feature];
      }
    });
  };

  const handleToggleCategory = (
    category: string,
    checked: boolean | "indeterminate"
  ) => {
    if (disabled) return;

    const categoryFeatures = featuresByCategory[category];
    setSelectedFeatures((prev) => {
      if (checked === true) {
        const newFeatures = [...prev];
        categoryFeatures.forEach((feature) => {
          if (!newFeatures.includes(feature)) {
            newFeatures.push(feature);
          }
        });
        return newFeatures;
      } else {
        return prev.filter((feature) => !categoryFeatures.includes(feature));
      }
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setSuccessMessage(null);
    setErrorMessage(null);

    try {
      await onSave(selectedFeatures);
      setSuccessMessage("Funcionalidades atualizadas com sucesso!");
    } catch (err: any) {
      console.error("Error saving features:", err);
      setErrorMessage(err.message || "Erro ao atualizar funcionalidades");
    } finally {
      setSaving(false);
    }
  };

  const isCategoryChecked = (category: string) => {
    const categoryFeatures = featuresByCategory[category];
    return categoryFeatures.every((feature) =>
      selectedFeatures.includes(feature)
    );
  };

  const isCategoryIndeterminate = (category: string) => {
    const categoryFeatures = featuresByCategory[category];
    const checkedCount = categoryFeatures.filter((feature) =>
      selectedFeatures.includes(feature)
    ).length;
    return checkedCount > 0 && checkedCount < categoryFeatures.length;
  };

  if (disabled && disabledMessage) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        {disabledMessage}
      </Alert>
    );
  }

  return (
    <div>
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage(null)}
        >
          {successMessage}
        </Alert>
      )}

      {errorMessage && (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          onClose={() => setErrorMessage(null)}
        >
          {errorMessage}
        </Alert>
      )}

      {/* Summary */}
      <div className="mb-2 flex gap-1 wrap">
        <Typography variant="body2" className="text-[#676A70] mb-2">
          {selectedFeatures.length} de {availableFeatures.length}{" "}
          funcionalidades selecionadas
        </Typography>
      </div>

      {/* Features by Category */}
      {Object.entries(featuresByCategory).map(([category, features]) => (
        <div key={category} className="mb-4">
          <Field orientation="horizontal">
            <StyledCheckbox
              checked={isCategoryChecked(category)}
              onCheckedChange={(checked) =>
                handleToggleCategory(category, checked)
              }
              disabled={disabled}
            />
            <Typography variant="subtitle1">{category}</Typography>
          </Field>
          <FieldGroup className="ml-3 mt-4">
            {features.map((feature) => (
              <Field key={feature} orientation="horizontal">
                <StyledCheckbox
                  checked={selectedFeatures.includes(feature)}
                  onCheckedChange={() => handleToggleFeature(feature)}
                  disabled={disabled}
                />
                <div className="flex items-center">
                  <Typography variant="body2">
                    {FEATURE_LABELS[feature] || feature}
                  </Typography>
                  {feature.startsWith("manage_") && (
                    <Chip label="Admin" size="small" color="warning" />
                  )}
                </div>
              </Field>
            ))}
          </FieldGroup>
        </div>
      ))}

      {/* Save Button */}
      {!disabled && (
        <div className="mt-3 flex gap-2">
          <Button onClick={handleSave} disabled={saving || loading}>
            {saving && <CircularProgress size={20} />}
            {saving ? "Salvando..." : "Salvar Alterações"}
          </Button>
          <Button
            variant="outline"
            onClick={() => setSelectedFeatures(initialSelected)}
            disabled={saving || loading}
          >
            Cancelar
          </Button>
        </div>
      )}
    </div>
  );
}
